import { Navigate } from 'react-router-dom';
import { Button } from '@/components/Button/Button';
import { PriceBreakdown } from '@/components/PriceBreakdown/PriceBreakdown';
import { Thumb } from '@/components/Thumb/Thumb';
import { useOrder } from '@/context/OrderContext';
import { formatPrice } from '@/utils/format';
import { lineBreakdown } from '@/utils/pricing';
import { paths } from '@/routes/paths';
import styles from './Confirmation.module.css';

export function Confirmation() {
  const { order } = useOrder();

  // Landing here directly (bookmark, refresh in a new session) has nothing to show.
  if (!order) return <Navigate to={paths.home} replace />;

  const firstName = order.name.split(' ')[0] || 'you';
  const emailTo = `${firstName.toLowerCase()}@gmail.com`;

  // Digital books are entitlements on a playTUB login, not parcels — so the
  // confirmation says where to read them rather than when they'll turn up.
  const isDigital = order.fulfilment === 'digital';

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div className={styles.check} aria-hidden="true">
          ✓
        </div>
        <h1 className={styles.title}>Order placed!</h1>
        <div className={styles.meta}>
          Order <strong>{order.id}</strong> · {isDigital ? 'ready' : 'arriving'} {order.eta} · paid
          via {order.payLabel}
        </div>
      </div>

      <div className={styles.receipt}>
        <div className={styles.receiptHead}>
          <div className={styles.from}>
            From: <strong className={styles.fromStrong}>orders@tubmarket.com</strong> · To:{' '}
            {emailTo}
          </div>
          <div className={styles.subject}>Your TUBMarket order {order.id} is confirmed 🎉</div>
        </div>

        <div className={styles.receiptBody}>
          <p className={styles.greeting}>
            Hi {firstName} — thanks for shopping small.{' '}
            {isDigital
              ? "Open playTUB and you'll find these waiting:"
              : "Here's what's headed your way:"}
          </p>

          <ul className={styles.items}>
            {order.items.map((item) => (
              <li key={item.key ?? item.id ?? item.name} className={styles.item}>
                <Thumb
                  letter={item.letter}
                  tint={item.tint}
                  deep={item.deep}
                  src={item.image_url}
                  size="xs"
                />
                <div className={styles.itemName}>
                  {item.name}
                  <PriceBreakdown item={item} currency={order.currency} />
                  <div className={styles.itemQty}>
                    {item.kind === 'renewal' ? 'Renewed · one more term' : `Qty ${item.qty}`}
                  </div>
                </div>
                <div className={styles.itemTotal}>
                  {formatPrice(lineBreakdown(item).lineTotal, item.currency ?? order.currency)}
                </div>
              </li>
            ))}
          </ul>

          <div className={styles.total}>
            <span>Total paid</span>
            <span>{formatPrice(order.total, order.currency)}</span>
          </div>

          {isDigital && order.username && (
            <div className={styles.access}>
              Added to your playTUB account <strong>{order.username}</strong>. Sign in there with
              the same login to start reading.
            </div>
          )}

          {order.entitlements_recorded === false && (
            <div className={styles.syncWarning} role="status">
              Your payment went through and this order is confirmed, but we couldn&apos;t reach
              playTUB to unlock the books just yet. They&apos;ll appear shortly — contact support
              with order {order.id} if they don&apos;t.
            </div>
          )}
        </div>
      </div>

      <div className={styles.actions}>
        <Button to={paths.orders}>Your purchases</Button>
        <Button variant="dark" to={paths.home}>
          Keep browsing →
        </Button>
      </div>
    </div>
  );
}
